import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./BibtexCitationContent.css";

// ---- tiny scroll-reveal wrapper (mirrors the reference design's fade-up-on-scroll) ----
const Reveal = ({ as: Tag = "div", className = "", style, children, id }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      id={id}
      style={style}
      className={`bib-reveal${visible ? " bib-visible" : ""}${className ? ` ${className}` : ""}`}
    >
      {children}
    </Tag>
  );
};

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const BraceIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M8 3c-2 0-3 1-3 3v4c0 1-1 2-2 2 1 0 2 1 2 2v4c0 2 1 3 3 3M16 3c2 0 3 1 3 3v4c0 1 1 2 2 2-1 0-2 1-2 2v4c0 2-1 3-3 3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const KeyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="8" cy="15" r="4" />
    <path d="M10.5 12.5L20 3M17 6l3 3M14 9l2.5 2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 20l1-4L16 5l3 3L8 19l-4 1z" strokeLinejoin="round" />
  </svg>
);

const LinkIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 15l6-6M10 6l1.5-1.5a4 4 0 015.7 5.7L15.5 12M14 18l-1.5 1.5a4 4 0 01-5.7-5.7L8.5 12" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 3v13m0 0l-5-5m5 5l5-5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 20h16" strokeLinecap="round" />
  </svg>
);

const BookIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 5.5A2.5 2.5 0 016.5 3H12v18H6.5A2.5 2.5 0 014 18.5v-13z" strokeLinejoin="round" />
    <path d="M20 5.5A2.5 2.5 0 0017.5 3H12v18h5.5a2.5 2.5 0 002.5-2.5v-13z" strokeLinejoin="round" />
  </svg>
);

const BIB_ENTRY_TYPES = [
  { tag: "@article", title: "Journal Article Entries", desc: "Use the @article type when your source came from a journal, so the entry keeps the journal name, volume, and issue in place." },
  { tag: "@book", title: "Book Entries", desc: "Use the @book type for full books and textbooks, capturing the publisher, edition, and place of publication alongside the author." },
  { tag: "@inproceedings", title: "Conference Paper Entries", desc: "Use @inproceedings for conference papers, so the entry records the conference name, location, and page range correctly." },
  { tag: "@misc", title: "Web Page and Online Source Entries", desc: "Use @misc when citing a website, blog post, or online resource. This is the type most BibTeX URL citation requests turn into." },
  { tag: "@techreport", title: "Report Entries", desc: "Use @techreport for institutional or government reports, keeping the issuing organization and report number with the citation." },
  { tag: "@phdthesis", title: "Thesis and Dissertation Entries", desc: "Use @phdthesis or @mastersthesis for academic theses, so the school and degree level stay attached to the source." },
];

const BIB_STEPS = [
  { title: "Choose Your Entry Type", desc: "Select article, book, conference paper, report, thesis, or let the generator detect it from your source." },
  { title: "Select the Identifier Type", desc: "Choose URL, DOI, ArXiv ID, PubMed ID, or title, depending on what you have on hand." },
  { title: "Paste the Source Details", desc: "Enter the link, identifier, or title of the source you want to cite in BibTeX." },
  { title: "Review the Entry", desc: "Check that the author, title, year, and key look correct before adding the entry to your .bib file." },
  { title: "Copy It", desc: "Copy the full BibTeX block, or export it, and paste it straight into your reference file." },
];

const BIB_SOURCE_TYPES = [
  { tag: "Website", title: "Website BibTeX Generator", desc: "Turn a webpage, blog, or online article into a properly tagged @misc entry with a working BibTeX URL citation." },
  { tag: "Book", title: "Book BibTeX Generator", desc: "Create @book entries for print books, textbooks, and edited volumes with publisher and edition included." },
  { tag: "Journal Article", title: "Journal Article BibTeX Generator", desc: "Generate @article citations for scholarly and DOI-backed research, journal name and volume included." },
  { tag: "Video", title: "Video BibTeX Generator", desc: "Cite lectures, webinars, and recorded talks as @misc entries with the platform and access date recorded." },
  { tag: "Report", title: "Report BibTeX Generator", desc: "Build @techreport entries for white papers and government or institutional publications." },
  { tag: "Conference Paper", title: "Conference Paper BibTeX Generator", desc: "Format @inproceedings entries with conference name, location, and page numbers in place." },
];

const BIB_WHY_ITEMS = [
  { icon: <BraceIcon />, title: "Correct Entry Types", desc: "Get the right BibTeX entry type for articles, books, reports, and conference papers, matched to your source." },
  { icon: <KeyIcon />, title: "Automatic Citation Keys", desc: "Every entry gets a clean, readable key you can reuse across a whole document without renaming it later." },
  { icon: <EditIcon />, title: "Manual Editing", desc: "Adjust any field by hand for sources with incomplete metadata, so nothing gets left as a guess." },
  { icon: <LinkIcon />, title: "URL and DOI Support", desc: "Paste a link or a DOI and get a working BibTeX URL citation without formatting it field by field yourself." },
  { icon: <DownloadIcon />, title: "Export-Ready Output", desc: "Copy your entry straight into a .bib file, or export it for use with Overleaf and other LaTeX editors." },
  { icon: <BookIcon />, title: "Bibliography Support", desc: "Keep building entries for longer papers and multi-source projects without losing track of earlier citations." },
];

const BIB_PLAN_COMPARISON = [
  { feature: "Generate @article citations", free: true, premium: true },
  { feature: "Generate @book citations", free: true, premium: true },
  { feature: "Generate @misc and @techreport citations", free: true, premium: true },
  { feature: "Copy individual citations", free: true, premium: true },
  { feature: "Save .bib entries to a library", free: "limited", premium: "Unlimited" },
  { feature: "Export full .bib file", free: "limited", premium: "Unlimited" },
  { feature: "Bulk citation import", free: null, premium: true },
];

const BIB_FAQS = [
  { q: "Is this citation generator free to use?", a: "Yes. You can paste a URL, DOI, ArXiv ID, PubMed ID, or title and generate a BibTeX citation without paying or creating an account." },
  { q: "Which identifier types can I paste into the generator?", a: "You can enter a web URL, a DOI, an ArXiv ID, a PubMed ID, or simply the title of the source you want to cite." },
  { q: "Can I create a BibTeX citation from a website?", a: "Yes. Paste the page URL and the generator builds a BibTeX URL citation as a @misc entry with the author, title, and access date filled in." },
  { q: "What BibTeX entry types are supported?", a: "The generator supports @article, @book, @inproceedings, @misc, @techreport, and thesis entry types, covering the sources most papers cite." },
  { q: "Do I need to install LaTeX to use this tool?", a: "No. The generator runs in your browser and produces plain BibTeX text you can paste into any .bib file, whether you use LaTeX locally or an online editor." },
  { q: "Can I edit a citation after it is generated?", a: "Yes. Every field in the output box can be edited by hand, which is useful when a source has incomplete or unusual metadata." },
  { q: "How does the generator create a citation key?", a: "It builds a short, readable key from the author or source name and the publication year, so you can reference it consistently with \\cite{} throughout your document." },
  { q: "Can I generate a citation from just a title?", a: "Yes. If you do not have a URL, DOI, or ID on hand, switch the identifier type to Title and enter the source name directly." },
  { q: "Is my source information stored anywhere?", a: "No account is required to generate a citation, and your input is used only to build the entry you asked for." },
  { q: "Can I export more than one citation at a time?", a: "Yes, for longer projects the generator supports building multiple entries and exporting them together as a single .bib file." },
  { q: "What is the difference between @article and @misc?", a: "@article is meant for journal publications with a named journal, volume, and pages, while @misc covers sources like websites and informal publications that do not fit a stricter type." },
  { q: "Can I cite a conference paper with this tool?", a: "Yes. Choose the @inproceedings entry type so the conference name, location, and page range are recorded correctly." },
  { q: "Does the generator work with Overleaf?", a: "Yes. The output is standard BibTeX, so you can paste it directly into a .bib file inside an Overleaf project or any other LaTeX editor." },
  { q: "What if the source I am citing has no listed author?", a: "You can leave the author field as a placeholder and edit it manually, or cite the publishing organization instead, which is common for @misc entries." },
  { q: "Can I cite a thesis or dissertation in BibTeX?", a: "Yes. Use the @phdthesis or @mastersthesis entry type to keep the school and degree level attached to the citation." },
  { q: "How do I cite a report or white paper in BibTeX?", a: "Choose the @techreport entry type, which keeps the issuing organization and report number alongside the standard fields." },
  { q: "Can I copy just the citation key without the full entry?", a: "Yes, the citation key is shown as the first line of the generated entry and can be copied on its own for use in \\cite{} commands." },
  { q: "Will the generator detect the entry type automatically?", a: "When you provide a DOI or a structured identifier, the generator can suggest a likely entry type, though you can always change it manually." },
  { q: "What should I double-check before using a generated citation?", a: "Confirm the author names, title, year, and identifier are accurate, since source metadata online is not always complete or correctly labeled." },
  { q: "Is there a limit to how many citations I can generate?", a: "The free version covers everyday use, while the premium version adds unlimited saving and export for larger, multi-source projects." },
];

const FAQ_BATCH = 5;

const BibFaqList = () => {
  const [revealed, setRevealed] = useState(FAQ_BATCH);
  const [openSet, setOpenSet] = useState(() => new Set());

  const open = (idx) => {
    const wasOpen = openSet.has(idx);
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (wasOpen) next.delete(idx);
      else next.add(idx);
      return next;
    });
    // Reference design only pulls in the next batch when *opening* an item, not when closing one.
    if (!wasOpen) {
      setRevealed((prev) => (prev < BIB_FAQS.length ? Math.min(prev + FAQ_BATCH, BIB_FAQS.length) : prev));
    }
  };

  const visible = BIB_FAQS.slice(0, revealed);
  const done = revealed >= BIB_FAQS.length;

  return (
    <>
      <div className="bib-faq-list">
        {visible.map((item, idx) => {
          const isOpen = openSet.has(idx);
          return (
            <div className={`bib-faq-item${isOpen ? " bib-open" : ""}`} key={item.q}>
              <button type="button" className="bib-faq-question" aria-expanded={isOpen} onClick={() => open(idx)}>
                <span>{item.q}</span>
                <span className="bib-faq-icon" />
              </button>
              <div className="bib-faq-answer">
                <p>{item.a}</p>
              </div>
            </div>
          );
        })}
      </div>
      {!done && (
        <div className="bib-faq-more-wrap">
          <button type="button" className="bib-faq-more-btn" onClick={() => setRevealed((prev) => Math.min(prev + FAQ_BATCH, BIB_FAQS.length))}>
            Show more questions
          </button>
        </div>
      )}
      <p className="bib-faq-count">Showing {revealed} of {BIB_FAQS.length} questions</p>
    </>
  );
};

const BibtexCitationContent = () => {
  return (
    <div className="bib-lp">
      <section className="bib-section bib-intro">
        <div className="bib-wrap">
          <Reveal as="h1">Free BibTeX Citation Generator: Article, Book & Web Entries for LaTeX</Reveal>
          <Reveal as="p" className="bib-lead">Need to cite a paper, book, or web page in a .bib file? Our BibTeX citation generator reads your source details and builds a properly formatted entry, so you spend less time matching braces and commas and more time writing. Paste a URL, DOI, ArXiv ID, or title, and generate a BibTeX citation ready for LaTeX, Overleaf, or any reference manager that reads .bib files.</Reveal>
          <Reveal className="bib-hero-buttons">
            <a href="#citation-tool" className="bib-btn-solid">Generate My BibTeX Citation</a>
            <a href="#bib-entry-types" className="bib-btn-ghost">View Entry Types</a>
          </Reveal>
          <Reveal as="ul" className="bib-pill-row">
            <span><CheckIcon />Create BibTeX citations faster</span>
            <span><CheckIcon />Supports major entry types</span>
            <span><CheckIcon />Build citation keys automatically</span>
            <span><CheckIcon />Edit source details manually</span>
            <span><CheckIcon />Copy, save, or export your .bib file</span>
          </Reveal>
        </div>
      </section>

      <section className="bib-section bib-band-soft">
        <div className="bib-wrap">
          <Reveal as="p" className="bib-section-eyebrow">The Basics</Reveal>
          <Reveal as="h2" className="bib-section-title">What Is a BibTeX Citation?</Reveal>
          <Reveal className="bib-quote-card">
            <p>"A BibTeX citation is a small block of plain text, wrapped in curly braces, that stores everything a reference needs: an entry type, a citation key, and fields like author, title, and year, so LaTeX can format it consistently every time."</p>
          </Reveal>
          <Reveal className="bib-two-col">
            <div className="bib-info-card">
              <h3>How It Works</h3>
              <p>Instead of typing out every field by hand, you enter the source details once and the generator arranges them into the entry type your document needs, whether that is @article, @book, @inproceedings, or @misc.</p>
            </div>
            <div className="bib-info-card">
              <h3>Why It Helps</h3>
              <p>A BibTeX citation generator does not save time by accident. It keeps your bibliography file consistent, reduces mismatched braces and missing fields, and gives every source in your .bib file the same structure.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bib-section" id="bib-entry-types">
        <div className="bib-wrap">
          <Reveal as="p" className="bib-section-eyebrow">Pick Your Entry Type</Reveal>
          <Reveal as="h2" className="bib-section-title">Which BibTeX Entry Types Can You Generate?</Reveal>
          <Reveal as="p" className="bib-section-sub">Different sources call for different BibTeX entry types. Choose the one that matches what you are citing.</Reveal>

          <div className="bib-entry-grid">
            {BIB_ENTRY_TYPES.map((e) => (
              <Reveal key={e.tag} className="bib-entry-card">
                <span className="bib-entry-tag">{e.tag}</span>
                <h3>{e.title}</h3>
                <p>{e.desc}</p>
                <div className="bib-entry-links">
                  <a href="#citation-tool">Generate {e.tag} entry</a>
                  <a href="#bib-examples">See example</a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bib-section bib-band-soft" id="bib-how-it-works">
        <div className="bib-wrap">
          <Reveal as="p" className="bib-section-eyebrow">Five Simple Steps</Reveal>
          <Reveal as="h2" className="bib-section-title">How to Create a BibTeX Citation</Reveal>

          <div className="bib-steps-list">
            {BIB_STEPS.map((step, idx) => (
              <Reveal className="bib-step-item" key={step.title}>
                <div className="bib-step-num">{idx + 1}</div>
                <div><h3>{step.title}</h3><p>{step.desc}</p></div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bib-section">
        <div className="bib-wrap">
          <Reveal as="p" className="bib-section-eyebrow">By Source</Reveal>
          <Reveal as="h2" className="bib-section-title">Generate BibTeX Citations by Source Type</Reveal>
          <Reveal as="p" className="bib-section-sub">Whatever you are citing, the generator maps it to the right BibTeX fields automatically.</Reveal>

          <div className="bib-source-grid">
            {BIB_SOURCE_TYPES.map((item) => (
              <Reveal key={item.tag} className="bib-source-card">
                <span className="bib-source-tag">{item.tag}</span>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bib-section bib-band-soft" id="bib-examples">
        <div className="bib-wrap">
          <Reveal as="p" className="bib-section-eyebrow">See It in Action</Reveal>
          <Reveal as="h2" className="bib-section-title">Example BibTeX Citations</Reveal>

          <div className="bib-example-grid">
            <Reveal className="bib-example-card">
              <div className="bib-example-tag-row">
                <span className="bib-type">Journal Article</span>
                <span className="bib-format">@article</span>
              </div>
              <pre>
                <span className="bib-p">@article{"{"}</span><span className="bib-k">smith2022research</span><span className="bib-p">,</span>{"\n"}
                {"  "}<span className="bib-k">author</span> <span className="bib-p">=</span> <span className="bib-s">{"{Smith, J. and Lee, R.}"}</span><span className="bib-p">,</span>{"\n"}
                {"  "}<span className="bib-k">title</span>{"  "}<span className="bib-p">=</span> <span className="bib-s">{"{Understanding AI research}"}</span><span className="bib-p">,</span>{"\n"}
                {"  "}<span className="bib-k">journal</span> <span className="bib-p">=</span> <span className="bib-s">{"{Journal of Technology}"}</span><span className="bib-p">,</span>{"\n"}
                {"  "}<span className="bib-k">year</span>{"   "}<span className="bib-p">=</span> <span className="bib-s">{"{2022}"}</span><span className="bib-p">,</span>{"\n"}
                {"  "}<span className="bib-k">volume</span> <span className="bib-p">=</span> <span className="bib-s">{"{31}"}</span><span className="bib-p">,</span>{"\n"}
                {"  "}<span className="bib-k">pages</span>{"  "}<span className="bib-p">=</span> <span className="bib-s">{"{108--115}"}</span>{"\n"}
                <span className="bib-p">{"}"}</span>
              </pre>
              <p className="bib-example-cite-line">Cited in text as \cite{"{smith2022research}"}</p>
            </Reveal>

            <Reveal className="bib-example-card">
              <div className="bib-example-tag-row">
                <span className="bib-type">Website</span>
                <span className="bib-format">@misc</span>
              </div>
              <pre>
                <span className="bib-p">@misc{"{"}</span><span className="bib-k">researchpal2026aitools</span><span className="bib-p">,</span>{"\n"}
                {"  "}<span className="bib-k">author</span> <span className="bib-p">=</span> <span className="bib-s">{"{ResearchPal}"}</span><span className="bib-p">,</span>{"\n"}
                {"  "}<span className="bib-k">title</span>{"  "}<span className="bib-p">=</span> <span className="bib-s">{"{How AI tools help students write better}"}</span><span className="bib-p">,</span>{"\n"}
                {"  "}<span className="bib-k">year</span>{"   "}<span className="bib-p">=</span> <span className="bib-s">{"{2026}"}</span><span className="bib-p">,</span>{"\n"}
                {"  "}<span className="bib-k">howpublished</span> <span className="bib-p">=</span> <span className="bib-s">{"{\\url{researchpal.co}}"}</span><span className="bib-p">,</span>{"\n"}
                {"  "}<span className="bib-k">note</span>{"   "}<span className="bib-p">=</span> <span className="bib-s">{"{Accessed 2026-07-30}"}</span>{"\n"}
                <span className="bib-p">{"}"}</span>
              </pre>
              <p className="bib-example-cite-line">Cited in text as \cite{"{researchpal2026aitools}"}</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bib-section">
        <div className="bib-wrap">
          <Reveal as="p" className="bib-section-eyebrow">The Payoff</Reveal>
          <Reveal as="h2" className="bib-section-title">Why Use Our Free BibTeX Citation Generator?</Reveal>

          <div className="bib-benefit-grid">
            {BIB_WHY_ITEMS.map((w) => (
              <Reveal key={w.title} className="bib-benefit-card">
                <div className="bib-benefit-icon">{w.icon}</div>
                <h3>{w.title}</h3>
                <p>{w.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bib-section bib-band-soft" id="bib-pricing">
        <div className="bib-wrap">
          <Reveal as="p" className="bib-section-eyebrow">Compare Plans</Reveal>
          <Reveal as="h2" className="bib-section-title">Free and Premium Features</Reveal>

          <Reveal className="bib-price-table-wrap">
            <table className="bib-price-table">
              <thead>
                <tr><th>Feature</th><th>Free Version</th><th>Premium Version</th></tr>
              </thead>
              <tbody>
                {BIB_PLAN_COMPARISON.map((row) => (
                  <tr key={row.feature}>
                    <td>{row.feature}</td>
                    <td className={row.free === true ? "bib-check-yes" : row.free === "limited" ? "bib-check-limited" : "bib-check-dash"}>
                      {row.free === true ? "✓" : row.free === "limited" ? "Limited" : "—"}
                    </td>
                    <td className={row.premium === true ? "bib-check-yes" : "bib-check-unlimited"}>
                      {row.premium === true ? "✓" : row.premium}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>
        </div>
      </section>

      <section className="bib-section" id="bib-ai-research-assistant">
        <div className="bib-wrap">
          <Reveal as="p" className="bib-section-eyebrow">AI Research Assistant</Reveal>
          <Reveal as="h2" className="bib-section-title">AI Research Assistant for BibTeX and Research Workflows</Reveal>
          <Reveal className="bib-two-col">
            <div className="bib-info-card">
              <p style={{ marginBottom: "12px" }}>BibTeX is particularly useful for researchers writing in LaTeX or working with platforms such as Overleaf. ResearchPal's BibTeX generator can turn source details such as a URL, DOI, ArXiv ID or title into a BibTeX entry that can be added to a .bib file.</p>
              <p style={{ marginBottom: "12px" }}>But managing references is only one part of a research workflow.</p>
              <p>The ResearchPal AI Research Assistant can help you investigate the research connected to those references. You can explore a topic, discuss papers, understand findings, ask questions about sources and develop ideas before adding the relevant references to your LaTeX project.</p>
            </div>
            <div className="bib-info-card">
              <h3>Combine BibTeX With an AI Research Assistant</h3>
              <p style={{ marginBottom: "10px" }}>Use the Agent to:</p>
              <ul className="bib-ai-list">
                <li>Explore research topics before collecting references</li>
                <li>Understand papers you intend to cite</li>
                <li>Discuss findings and methodology</li>
                <li>Identify useful sources for a research question</li>
                <li>Clarify technical or academic concepts</li>
                <li>Plan literature review topics</li>
                <li>Connect research discussions with your citation workflow</li>
              </ul>
            </div>
          </Reveal>
          <Reveal className="bib-ai-cta">
            <Link to="/ai-agent" className="bib-btn-solid">Try AI Research Assistant</Link>
          </Reveal>
        </div>
      </section>

      <section className="bib-section" id="bib-free-tools">
        <div className="bib-wrap">
          <Reveal as="p" className="bib-section-eyebrow">More Free Tools</Reveal>
          <Reveal as="h2" className="bib-section-title">Explore ResearchPal's Free Tools</Reveal>

          <div className="bib-free-tools-group" style={{ marginTop: "36px" }}>
            <h3 className="bib-free-tools-group-title">ResearchPal Free Tools</h3>
            <Reveal className="bib-source-grid">
              <Link className="bib-free-tool-card" to="/paraphraser">
                <div><h3>Paraphraser</h3><p>Rewrite sentences and paragraphs while keeping the meaning.</p></div>
                <span className="bib-tool-arrow">→</span>
              </Link>
              <Link className="bib-free-tool-card" to="/ai-agent">
                <div><h3>AI Agent</h3><p>Chat with an AI assistant built for research tasks.</p></div>
                <span className="bib-tool-arrow">→</span>
              </Link>
              <Link className="bib-free-tool-card" to="/search">
                <div><h3>Search Papers</h3><p>Find and explore academic papers across sources.</p></div>
                <span className="bib-tool-arrow">→</span>
              </Link>
              <Link className="bib-free-tool-card" to="/essay-writer">
                <div><h3>Essay Writer</h3><p>Draft structured essays with AI assistance.</p></div>
                <span className="bib-tool-arrow">→</span>
              </Link>
              <Link className="bib-free-tool-card" to="/literature-review">
                <div><h3>Literature Review</h3><p>Summarize and organize sources for your review.</p></div>
                <span className="bib-tool-arrow">→</span>
              </Link>
              <Link className="bib-free-tool-card" to="/paper-insights">
                <div><h3>Paper Insights</h3><p>Get quick, structured insights from any paper.</p></div>
                <span className="bib-tool-arrow">→</span>
              </Link>
            </Reveal>
          </div>

          <div className="bib-free-tools-group">
            <h3 className="bib-free-tools-group-title">Other Free Citation Generators</h3>
            <Reveal className="bib-source-grid">
              <Link className="bib-free-tool-card" to="/citation-generator/apa">
                <div><h3>APA Citation Generator</h3><p>Format references in APA style instantly.</p></div>
                <span className="bib-tool-arrow">→</span>
              </Link>
              <Link className="bib-free-tool-card" to="/citation-generator/harvard">
                <div><h3>Harvard Citation Generator</h3><p>Create author-date Harvard references.</p></div>
                <span className="bib-tool-arrow">→</span>
              </Link>
              <Link className="bib-free-tool-card" to="/citation-generator/mla">
                <div><h3>MLA Citation Generator</h3><p>Generate MLA in-text and Works Cited entries.</p></div>
                <span className="bib-tool-arrow">→</span>
              </Link>
              <Link className="bib-free-tool-card" to="/citation-generator/chicago">
                <div><h3>Chicago Citation Generator</h3><p>Build Notes-Bibliography or Author-Date citations.</p></div>
                <span className="bib-tool-arrow">→</span>
              </Link>
              <Link className="bib-free-tool-card" to="/citation-generator/metadata">
                <div><h3>Metadata Lookup</h3><p>View a source's structured details before citing it.</p></div>
                <span className="bib-tool-arrow">→</span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bib-section">
        <div className="bib-wrap">
          <Reveal className="bib-cta-banner">
            <h2>Ready to Generate Your BibTeX Citation?</h2>
            <p>Paste a link, DOI, ArXiv ID, or title and get a formatted BibTeX entry in seconds, free, with no sign-up required to try.</p>
            <a href="#citation-tool" className="bib-btn-on-dark">Generate My BibTeX Citation</a>
          </Reveal>
        </div>
      </section>

      <section className="bib-section" id="bib-faq">
        <div className="bib-wrap">
          <Reveal as="p" className="bib-section-eyebrow">Shortcuts</Reveal>
          <Reveal as="h2" className="bib-section-title">Frequently Asked Questions</Reveal>
          <BibFaqList />
        </div>
      </section>
    </div>
  );
};

export default BibtexCitationContent;
